import axios from "axios";
import { normalizeDeckData } from "../deckProvider/normalizeCard";
import { extractError } from "../../utils/apiErrorUtils";

const pokemonTCGapiURL =
  process.env.REACT_APP_API_URL || "http://localhost:5000";

export const getPokemonSets = async () => {
  try {
    const { data } = await axios.get(`${pokemonTCGapiURL}/pokemontcg/sets`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to load Pokemon sets"));
  }
};

/**
 * A runaway stop, not a page budget: the server default is 250 per page, so this
 * allows a 5000-card set — an order of magnitude above the largest real one. It
 * only fires if the endpoint keeps claiming there is more.
 */
const MAX_CARD_PAGES = 20;

/**
 * Every card in one set, as a flat array.
 *
 * The endpoint is paged now (P1-25) — it used to answer with the whole matching
 * set, or with the entire catalog when asked without one — so this walks the
 * pages and concatenates. Callers still receive the complete set in a single
 * value because they genuinely need it: the set-completion view derives "add all
 * N missing to wishlist" from this list, and a truncated one would quietly
 * wishlist part of what is missing while reporting the smaller number as the
 * whole truth.
 *
 * A set is required. "Every card across every set" is not a view the app has —
 * the grid is browsed by set and global lookup is what searchPokemonCatalog is
 * for — so an accidental call without one fails loudly instead of starting to
 * page through the catalog.
 */
export const getPokemonCards = async (setId) => {
  if (!setId) {
    return Promise.reject("A set is required to load cards");
  }
  try {
    const cards = [];
    let page = 1;
    for (;;) {
      const { data } = await axios.get(
        `${pokemonTCGapiURL}/pokemontcg?set=${encodeURIComponent(setId)}&page=${page}`
      );
      const batch = data?.cards ?? [];
      cards.push(...batch);
      // Stopping on an empty batch as well as on `hasMore` keeps a server that
      // kept saying "more" without sending any from spinning this loop.
      if (!data?.hasMore || batch.length === 0) break;
      if (page >= MAX_CARD_PAGES) {
        console.warn(
          `[getPokemonCards] stopped at ${MAX_CARD_PAGES} pages for set ${setId}; ` +
            `showing ${cards.length} of ${data?.total ?? "?"} cards`
        );
        break;
      }
      page += 1;
    }
    return cards;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to load Pokemon cards"));
  }
};

/**
 * Global cross-set catalog search (#153). Matching happens server-side over the
 * whole catalog — the browser never downloads the card DB to search it — and the
 * active browsing set never constrains the result. Returns
 * { query, total, count, limit, cards } where `cards` is a bounded, slim,
 * relevance-ranked list the caller groups by set.
 */
export const searchPokemonCatalog = async (query, { limit } = {}) => {
  try {
    const params = new URLSearchParams({ q: query ?? "" });
    if (limit) params.set("limit", String(limit));
    const { data } = await axios.get(
      `${pokemonTCGapiURL}/pokemontcg/search?${params.toString()}`
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to search cards"));
  }
};

/**
 * A card's recorded price observations, for the history graph.
 *
 * The response carries each point's source and the currency that source quoted
 * (nothing is converted — there is no approved FX source), and days that were
 * never sampled are simply absent so the chart can draw them as gaps. A card
 * below the bulk threshold answers `tracked: false, reason: "bulk"` rather than
 * looking like missing data.
 */
export const getCardPriceHistory = async (cardId, { condition, days } = {}) => {
  try {
    const params = new URLSearchParams();
    if (condition) params.set("condition", condition);
    if (days) params.set("days", String(days));
    const qs = params.toString();
    const { data } = await axios.get(
      `${pokemonTCGapiURL}/pokemontcg/${cardId}/price-history${qs ? `?${qs}` : ""}`
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to load price history"));
  }
};

/**
 * One card, in full. getPokemonCards returns a slim projection for grid speed,
 * so the detail sheet refetches by id to get attacks, flavour text and the rest.
 */
export const getPokemonCardById = async (id) => {
  try {
    const { data } = await axios.get(
      `${pokemonTCGapiURL}/pokemontcg/${encodeURIComponent(id)}`
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to load card details"));
  }
};

export const savePokemonDeck = async (deck) => {
  try {
    const { data } = await axios.put(
      `${pokemonTCGapiURL}/pokemontcg/PdeckSave`,
      deck
    );
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to save deck"));
  }
};

export const getUserPokemonDecks = async (userId) => {
  try {
    const { data } = await axios.get(
      `${pokemonTCGapiURL}/pokemontcg/pokemonDecks/${userId}`
    );
    const normalizedDeck = normalizeDeckData(data);
    return normalizedDeck;
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to load decks"));
  }
};

export const deleteDeck = async (deckID) => {
  try {
    await axios.delete(`${pokemonTCGapiURL}/pokemontcg/pokemonDecks/${deckID}`);
    return "deleted successfully";
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to delete deck"));
  }
};
